import { apiClient } from '../core/api/apiClient';

export interface ProjectAIMetadata {
  keywords: string[];
  beneficiaries: string[];
  sdgs: string[];
  budget: string;
  timeline: string;
  organizations: string[];
  locations: string[];
  generated_at?: string;
  source_documents?: string[];
}


export interface ProjectProposedFields {
  project_name: string;
  category: string;
  sub_category: string;
  summary: string;
  objective: string;
  remarks: string;
  classification_note?: string;
  at: string;
}

export interface Project {
  _id: string;
  cause_id: string;
  cause_name: string;
  total_amount: number;
  donor_ids: Record<string, number>;
  category?: string;
  sub_category?: string;
  summary?: string;
  objective?: string;
  remarks?: string;
  ai_metadata?: ProjectAIMetadata;
  ai_proposed?: ProjectProposedFields;
  last_modified?: string;
  created_at?: string;
  documents?: string[];  // S3 keys uploaded via /v1/s3/projects/{cause_id}/upload-documents
}

export interface ProjectsListResponse {
  projects: Project[];
  pages: number;
  total: number;
}

// Core fields match the existing manual/JSON-autofill edit form exactly.
export interface ProjectCoreFields {
  category: string;
  sub_category: string;
  summary: string;
  objective: string;
  remarks: string;
}

// Extended fields are AI-derived supplementary metadata, persisted
// server-side to ai_metadata as soon as analysis completes (see
// analyzeProjectDocuments below) — they don't need a manual save.
export type ProjectExtendedFields = Omit<ProjectAIMetadata, 'generated_at' | 'source_documents'>;

export interface AnalyzeDocumentsResponse {
  project_id: string;
  core_fields: ProjectCoreFields;
  extended_fields: ProjectExtendedFields;
  document_text: string;
}

export const projectService = {
  getProjectsList: async (options: {
    page?: number;
    limit?: number;
    q?: string;
    sort_by?: string;
    sort_order?: number;
  } = {}): Promise<ProjectsListResponse> => {
    return apiClient<ProjectsListResponse>('/dashboard/projects', {
      method: 'GET',
      params: options,
    });
  },

  suggestCause: async (params: {
    cause_id?: string;
    cause_name?: string;
  }): Promise<{ cause_id: string; cause_name: string }> => {
    return apiClient<{ cause_id: string; cause_name: string }>('/dashboard/projects/suggest-cause', {
      method: 'GET',
      params,
    });
  },


  updateProject: async (
    id: string,
    updates: {
      category: string | null;
      sub_category: string | null;
      summary: string | null;
      objective: string | null;
      remarks: string | null;
      ai_session?: boolean;
      document_text?: string | null;
    }
  ): Promise<Project> => {
    return apiClient<Project>(`/dashboard/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  // Uploads project documents (PDF, DOCX, PPTX, TXT, images) for Gemini
  // analysis. The backend persists the AI-derived extended fields itself
  // (ai_metadata) and returns the core 5 fields for the user to review in
  // the existing edit form before saving via updateProject as usual.
  //
  // NOTE: this sends a multipart FormData body. If apiClient always sets
  // 'Content-Type: application/json', it needs a bypass for FormData
  // bodies — the browser must set the multipart boundary itself, e.g.:
  //
  //   const isFormData = options.body instanceof FormData;
  //   headers: isFormData ? undefined : { 'Content-Type': 'application/json', ...options.headers }
  analyzeProjectDocuments: async (
    id: string,
    files: File[]
  ): Promise<AnalyzeDocumentsResponse> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    return apiClient<AnalyzeDocumentsResponse>(`/dashboard/projects/${id}/analyze-documents`, {
      method: 'POST',
      body: formData,
    });
  },

  createProjectWithAI: async (data: {
    cause_id: string;
    cause_name: string;
    files: File[];
  }): Promise<Project> => {
    const formData = new FormData();
    formData.append('cause_id', data.cause_id);
    formData.append('cause_name', data.cause_name);
    data.files.forEach((file) => formData.append('files', file));

    return apiClient<Project>('/dashboard/projects/create-with-ai', {
      method: 'POST',
      body: formData,
    });
  },

  extractMetadataFromDocuments: async (
    files: File[]
  ): Promise<{ ai_proposed: ProjectProposedFields; ai_metadata: ProjectAIMetadata; document_text: string }> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    return apiClient<{ ai_proposed: ProjectProposedFields; ai_metadata: ProjectAIMetadata; document_text: string }>('/dashboard/projects/extract-metadata', {
      method: 'POST',
      body: formData,
    });
  },

  createProject: async (data: {
    cause_id: string;
    cause_name: string;
    category?: string;
    sub_category?: string;
    summary?: string;
    objective?: string;
    remarks?: string;
    ai_metadata?: ProjectAIMetadata;
    ai_proposed?: ProjectProposedFields;
    document_text?: string;
  }): Promise<Project> => {
    return apiClient<Project>('/dashboard/projects/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Uploads files to S3 under Projects/<cause_id>/. Called after the project
  // record has been created so the cause_id is guaranteed to exist in MongoDB.
  uploadProjectDocuments: async (
    causeId: string,
    files: File[]
  ): Promise<{ uploaded: string[]; failed: string[] }> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    return apiClient<{ uploaded: string[]; failed: string[] }>(
      `/v1/s3/projects/${causeId}/upload-documents`,
      { method: 'POST', body: formData }
    );
  },

  // Downloads a project document by streaming it through the backend.
  // The bucket is not publicly accessible, so we proxy through the API
  // rather than using presigned URLs that the browser can't reach.
  downloadProjectDocument: async (
    causeId: string,
    key: string,
    filename: string
  ): Promise<void> => {
    const response = await fetch(
      `/api/v1/s3/projects/${causeId}/download?key=${encodeURIComponent(key)}`,
      { credentials: 'include' }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `Download failed (${response.status})`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  // Deletes a project document from S3 and removes it from the project record.
  // Admin-only — the backend enforces the role check.
  deleteProjectDocument: async (
    causeId: string,
    key: string
  ): Promise<{ deleted: string }> => {
    return apiClient<{ deleted: string }>(
      `/v1/s3/projects/${causeId}/documents?key=${encodeURIComponent(key)}`,
      { method: 'DELETE' }
    );
  },

};

